import { type Cause, MessageBox, QurbaniCategory, Variant } from '@shamaazi/mytennights'
import { Widget } from './Widget'
import { type FC, type ReactElement, useCallback, useMemo, useState } from 'react'
import { type Currency, displayCurrency } from '@lib/services'
import { Button } from '@lib/components'
import IslamicReliefUK from '~/assets/islamic-relief-uk.svg'

const group1Text = 'Bangladesh, Bangladesh (Rohingya Refugees), Nepal, Pakistan and Somalia'
const group2Text = 'Afghanistan, Ethiopia, Philippines, South Africa, South Sudan and Sudan'
const group3Text = 'Albania, Bosnia & Herzegovina, Chechnya, Kosovo, Indonesia, North Macedonia, Sri Lanka'
const palestineQurbaniText = 'Give Qurbani in Palestine'

interface QurbaniWidgetProps {
  charityCauses: Cause[]
  checkoutCauses: Record<string, number>
  setCauses: (causes: Record<string, number>) => void
  currency: Currency
  nextPage?: string
}

export type QurbaniCounts = Record<string, number>

export interface QurbaniOption {
  key: keyof QurbaniCounts
  label: string
  price: number
}

export const QurbaniWidget: FC<QurbaniWidgetProps> = ({ charityCauses, setCauses, checkoutCauses, currency, nextPage = undefined }) => {
  // Memoize Qurbani options to prevent unnecessary recalculations
  const QURBANI_OPTIONS = useMemo<Cause[]>(() =>
    (charityCauses?.filter((cause: Cause) => cause.category === QurbaniCategory)) ?? [],
  [charityCauses])

  // Initialize counts state
  const [counts, setCounts] = useState<QurbaniCounts>(() => {
    const initialCounts: QurbaniCounts = {}
    for (const opt of QURBANI_OPTIONS) {
      // Check if this cause exists in plan.causes
      const existingAmount = checkoutCauses[opt.cause_id] || 0
      // Calculate quantity by dividing total amount by price per quantity
      initialCounts[opt.cause_id] = existingAmount > 0 ? Math.floor(existingAmount / opt.price_per_quantity) : 0
    }
    return initialCounts
  })

  const total = useMemo(() =>
    QURBANI_OPTIONS.reduce((sum, opt) => sum + counts[opt.cause_id] * opt.price_per_quantity, 0),
  [QURBANI_OPTIONS, counts]
  )

  // Memoize handlers
  const handleChange = useCallback((key: keyof QurbaniCounts, delta: number): void => {
    // Get current value before state update
    const currentCount = counts[key]
    const newCount = Math.max(0, currentCount + delta)

    // Update counts
    setCounts(prevCounts => ({
      ...prevCounts,
      [key]: newCount
    }))

    // Find the option outside
    const option = QURBANI_OPTIONS.find((opt) => opt.cause_id === key)
    if (option) {
      // Create the new causes object separately
      const newCauses = newCount === 0
        ? Object.fromEntries(
          Object.entries(checkoutCauses).filter(([k]) => k !== key)
        )
        : {
            ...checkoutCauses,
            [key]: newCount * option.price_per_quantity
          }

      // Set the plan with the new causes
      setCauses(newCauses)
    }
  }, [counts, QURBANI_OPTIONS, checkoutCauses, setCauses])

  const renderTooltip = (): ReactElement => {
    return <MessageBox infoIcon tooltipBorder variant={Variant.mtd} width={6} customBoxStyle="w-tooltip-box -left-80">
            <p className="font-normal text-xs">Countries supported for each group</p><br />
            <p className="font-normal text-xs"><strong>Group 1</strong><br />
              {group1Text}</p><br />
            <p className="font-normal text-xs"><strong>Group 2</strong><br />
              {group2Text}</p><br />
            <p className="font-normal text-xs"><strong>Group 3</strong><br />
              {group3Text}</p><br />
            <p className="font-normal text-xs"><strong>Palestine Qurbani</strong><br />
              {palestineQurbaniText}</p><br />
            <p className="font-normal text-xs">Tips will be applied to your Qurbani donation.</p><br/>
            <p className="font-normal text-xs">Qurbani donations are non-refundable. Visit our <a className="font-bold" href="https://about.shamaazi.com/mytendays/terms-and-conditions" target="_blank" rel="noopener noreferrer">T&Cs</a> and <a className="font-bold" href="https://about.shamaazi.com/mytendays/faq" target="_blank" rel="noopener noreferrer">FAQs</a> for more information.</p>
        </MessageBox>
  }

  return <>
        <Widget className="py-6">
            <div className="flex items-center justify-between">
                <h1 className="text-fg-blue-310 text-mtd-h2 font-bold">Set up your Qurbani</h1>
                {renderTooltip()}
            </div>
            <p className="text-mtd-secondary-5 text-xs font-medium py-4">
                Let us know where and how much you'd like your Qurbani to be and we will  arrange it for you. Various grouping have different cost so make sure you select the correct one for you.
            </p>

            <div className="flex items-center justify-start gap-3 border border-mtd-secondary-2 bg-mtd-secondary-1/20 rounded-xl px-1 py-3 mb-4">
            <img src={IslamicReliefUK} alt="Islamic Relief UK" className="w-10 h-10" />
            <p className="text-mtd-secondary-5 text-xs font-medium">Delivered in partnership with Islamic Relief UK</p>
            </div>

            <div className="w-full flex flex-col gap-y-2">
                {QURBANI_OPTIONS.map((opt) => (
                    <QurbaniOptionItem
                        key={opt.cause_id}
                        option={opt}
                        count={counts[opt.cause_id]}
                        onCountChange={handleChange}
                        currency={currency}
                    />
                ))}
                <div className="text-mtd-secondary-3 text-xs">Tips will be applied to your Qurbani donation.</div>
            </div>
        </Widget>
        {total !== 0 ? (
            <Widget backgroundColour="bg-mtd-secondary-5" className="py-6" radius="rounded-2xl">
                <div className="flex flex-row items-center justify-between w-full text-white">
                    <span className="text-mtd-h4 font-bold">Qurbani Total</span>
                    <span className="text-mtd-h2 font-bold">
                        {displayCurrency({ amount: total, currency, spaceAfterSymbol: true })}
                    </span>
                </div>
                <div className="text-mtd-indigo-3 text-xs font-medium w-full text-left mt-1">
                    Full amount will be taken on your first payment.
                </div>
            </Widget>
        ) : nextPage && (
            <div className="w-full flex justify-center items-center">
                <a
                    className="w-full text-mtd-secondary font-bold flex flex-row items-center justify-center hover:text-opacity-80 focus:bg-transparent text-mtd-h3"
                    href={nextPage}
                >
                    skip
                </a>
            </div>
        )}
    </>
}

const GetQurbaniTipText = (title: string): string => {
  const tipTextMap: Record<string, string> = {
    'Group 1': group1Text,
    'Group 2': group2Text,
    'Group 3': group3Text
  }

  return tipTextMap[title] ?? ''
}

// Component for individual Qurbani option
const QurbaniOptionItem: FC<{
  option: Cause
  count: number
  onCountChange: (key: keyof QurbaniCounts, delta: number) => void
  currency: Currency
}> = ({ option, count, onCountChange, currency }) => (
    <div className="flex flex-col items-center justify-between bg-mtd-secondary-1 rounded-xl px-4 py-3 border border-mtd-secondary-2">
      <div className="flex flex-row items-center justify-between w-full">
        <div className="flex flex-col items-start justify-start">
            <div className="text-mtd-secondary-4 round text-xs">{option.title}</div>
            <div className="text-mtd-secondary-4 text-mtd-h4 font-bold">
                {displayCurrency({
                  amount: option.price_per_quantity,
                  currency,
                  spaceAfterSymbol: true,
                  decimalPlaces: 0
                })}
            </div>
        </div>
        <QurbaniInput causeID={option.cause_id} count={count} onCountChange={onCountChange} />
      </div>
      <div className="flex flex-row items-center justify-between w-full">
        <div className="text-mtd-secondary-4 round text-xs">{GetQurbaniTipText(option.title)}</div>
      </div>
    </div>
)

export const QurbaniInput: FC<{ causeID: string, count: number, onCountChange: (key: keyof QurbaniCounts, delta: number) => void }> = ({ causeID, count, onCountChange }) => {
  return <div className="flex flex-row items-center gap-x-2">
        <Button
            variant="mtd-secondary-2"
            onClick={() => onCountChange(causeID, -1)}
            disabled={count === 0}
        >
            -
        </Button>
        <span className={`text-mtd-h3 font-bold w-6 text-center ${count === 0 ? 'text-mtd-secondary-3' : 'text-mtd-secondary-5'}`}>
            {count}
        </span>
        <Button
            variant="mtd-secondary-2"
            onClick={() => onCountChange(causeID, 1)}
        >
            +
        </Button>
    </div>
}
